import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Target, Zap, TrendingUp, Lightbulb } from "lucide-react";
import { CASE_STUDIES, waLink, BRAND } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

export default function CaseStudyDetail() {
  const { id } = useParams();
  const cs = CASE_STUDIES.find((c) => c.id === id);
  const idx = CASE_STUDIES.findIndex((c) => c.id === id);
  const next = CASE_STUDIES[(idx + 1) % CASE_STUDIES.length];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!cs) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#0a0a0a] px-6 text-center">
        <h1 className="font-display text-5xl uppercase text-white">
          Case study not found
        </h1>
        <Link
          to="/"
          data-testid="notfound-home-link"
          className="rounded-full bg-yellow-400 px-6 py-3 font-semibold text-black"
        >
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="case-study-detail">
      {/* Top bar */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="detail-logo">
            <img src={BRAND.logo} alt="Growth Therapy" className="h-12 w-auto sm:h-14" />
          </Link>
          <Link
            to="/#work"
            data-testid="detail-back-link"
            className="group inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:text-yellow-400"
          >
            <ArrowLeft
              size={15}
              className="transition-transform duration-300 group-hover:-translate-x-1"
            />
            All work
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
        {/* Heading */}
        <Reveal as="p" y={20} className="font-mono-accent mb-6 text-xs uppercase tracking-[0.3em] text-yellow-400">
          Case Study {cs.index} — {cs.category}
        </Reveal>
        <Reveal
          as="h1"
          delay={60}
          className="font-display max-w-4xl text-5xl uppercase leading-[0.92] text-white sm:text-7xl"
        >
          {cs.client}
        </Reveal>
        <Reveal
          as="p"
          delay={140}
          className="mt-5 max-w-2xl text-lg font-medium text-yellow-400"
        >
          {cs.headline}
        </Reveal>

        {/* Image */}
        <Reveal delay={80} className="mt-12">
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            <img
              src={cs.image}
              alt={cs.client}
              data-testid="detail-image"
              className="h-[320px] w-full object-cover sm:h-[520px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
            <div className="absolute bottom-6 left-6 flex flex-wrap gap-2">
              {cs.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/20 bg-black/40 px-3 py-1 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-white backdrop-blur-md"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Objective + Challenge */}
        <div className="mt-16 grid grid-cols-1 gap-12 border-t border-white/10 pt-14 md:grid-cols-2 md:gap-16">
          <Reveal>
            <div className="mb-5 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-yellow-400 text-black">
                <Target size={18} />
              </span>
              <h2 className="font-display text-3xl uppercase text-white">
                Objective
              </h2>
            </div>
            <p className="text-base leading-relaxed text-neutral-300" data-testid="detail-objective">
              {cs.objective}
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mb-5 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full border border-yellow-400/50 text-yellow-400">
                <Zap size={18} />
              </span>
              <h2 className="font-display text-3xl uppercase text-white">
                Challenge
              </h2>
            </div>
            <p className="text-base leading-relaxed text-neutral-300" data-testid="detail-challenge">
              {cs.challenge}
            </p>
          </Reveal>
        </div>

        {/* Strategy */}
        {cs.strategy && cs.strategy.length > 0 && (
          <div className="mt-16 border-t border-white/10 pt-14">
            <Reveal>
              <div className="mb-10 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full border border-yellow-400/50 text-yellow-400">
                  <Lightbulb size={18} />
                </span>
                <h2 className="font-display text-3xl uppercase text-white">
                  The Strategy
                </h2>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="detail-strategy">
              {cs.strategy.map((s, i) => (
                <Reveal
                  key={i}
                  delay={i * 90}
                  className="rounded-2xl border border-white/10 bg-[#111] p-6 transition-colors duration-500 hover:border-yellow-400/60"
                >
                  <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-yellow-400">
                    0{i + 1}
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                    {s.body}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="mt-16 border-t border-white/10 pt-14">
          <Reveal>
            <div className="mb-10 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-yellow-400 text-black">
                <TrendingUp size={18} />
              </span>
              <h2 className="font-display text-3xl uppercase text-white">
                Results
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="detail-results">
            {cs.results.map((r, i) => (
              <Reveal
                key={i}
                delay={i * 90}
                className="rounded-2xl border border-white/10 bg-[#111] p-6 transition-colors duration-500 hover:border-yellow-400/60"
              >
                <div className="font-display text-5xl leading-none text-yellow-400 sm:text-6xl">
                  {r.value}
                </div>
                <p className="mt-3 text-sm leading-snug text-neutral-400">
                  {r.label}
                </p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Reveal className="mt-20 overflow-hidden rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-[#141414] to-[#0a0a0a] px-6 py-14 text-center sm:px-16">
          <h3 className="mx-auto max-w-2xl font-display text-4xl uppercase leading-[0.95] text-white sm:text-5xl">
            Want results like these?
          </h3>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-book-button"
            className="group mt-8 inline-flex items-center gap-3 rounded-full bg-yellow-400 px-8 py-4 text-base font-semibold text-black transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_0_45px_rgba(250,204,21,0.55)]"
          >
            Book on WhatsApp
            <span className="grid h-7 w-7 place-items-center rounded-full bg-black text-yellow-400 transition-transform duration-300 group-hover:rotate-45">
              <ArrowUpRight size={16} />
            </span>
          </a>
        </Reveal>

        {/* Next case study */}
        <Link
          to={`/work/${next.id}`}
          data-testid="detail-next-link"
          className="group mt-16 flex items-center justify-between border-t border-white/10 pt-10"
        >
          <div>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-neutral-500">
              Next case study
            </p>
            <p className="font-display mt-2 text-3xl uppercase text-white transition-colors duration-300 group-hover:text-yellow-400 sm:text-4xl">
              {next.client}
            </p>
          </div>
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-white/15 text-neutral-300 transition-[transform,color,border-color] duration-300 group-hover:rotate-45 group-hover:border-yellow-400 group-hover:text-yellow-400">
            <ArrowUpRight size={20} />
          </span>
        </Link>
      </main>

      <WhatsAppFloat />
    </div>
  );
}
